import { TaxEstimatePreviewOutcome, type QuoteTaxRecalculationDto } from "client/api-v2";

function quotes(count: number): string {
  return count === 1 ? "1 quote" : `${count} quotes`;
}

function openQuotes(count: number): string {
  return count === 1 ? "1 open quote" : `${count} open quotes`;
}

export function liveQuoteTaxNotice(action: "Saving" | "Pushing", liveOrderCount: number): string {
  const which = liveOrderCount === 1 ? "it" : "one of them";
  return `${action} recalculates the tax estimate on ${openQuotes(
    liveOrderCount
  )}. Anyone editing ${which} will need to reload it.`;
}

export function liveQuoteTaxAwaitsPushNotice(liveOrderCount: number): string {
  const show = liveOrderCount === 1 ? "shows" : "show";
  return `Zuora holds this account's address, so ${openQuotes(
    liveOrderCount
  )} ${show} no tax estimate until you push it to Zuora.`;
}

export function taxRecalculationSummary(recalculations: readonly QuoteTaxRecalculationDto[]): string | undefined {
  if (recalculations.length === 0) {
    return undefined;
  }
  const recalculatedCount = recalculations.filter((recalculation) => recalculation.recalculated).length;
  const failedByOutcome = new Map<TaxEstimatePreviewOutcome, string[]>();
  for (const recalculation of recalculations.filter((recalculation) => !recalculation.recalculated)) {
    const outcome = recalculation.outcome ?? TaxEstimatePreviewOutcome.ZuoraPreviewUnavailable;
    failedByOutcome.set(outcome, [...(failedByOutcome.get(outcome) ?? []), recalculation.odNumber]);
  }

  const parts: string[] = [];
  if (recalculatedCount > 0) {
    parts.push(`Tax recalculated on ${quotes(recalculatedCount)}.`);
  }
  for (const [outcome, odNumbers] of failedByOutcome) {
    parts.push(
      `${odNumbers.length} couldn't be recalculated (${odNumbers.join(", ")}) — ${failureAdvice(
        outcome,
        odNumbers.length
      )}.`
    );
  }
  return parts.join(" ");
}

function failureAdvice(outcome: TaxEstimatePreviewOutcome, count: number): string {
  switch (outcome) {
    case TaxEstimatePreviewOutcome.NoAddressToCalculateTaxAgainst:
      return "the billing address isn't complete enough to calculate tax against";
    case TaxEstimatePreviewOutcome.SubscriptionNotYetInZuora:
      return count === 1
        ? "its subscription isn't in Zuora yet, so there's no renewal to price"
        : "their subscriptions aren't in Zuora yet, so there's no renewal to price";
    case TaxEstimatePreviewOutcome.PreviewedPriceDidNotMatchTheQuote:
      return "Zuora priced the quote differently from its own total. Raise this with engineering";
    default:
      return `open ${count === 1 ? "it" : "them"} and calculate again`;
  }
}

export function hasFailedTaxRecalculation(recalculations: readonly QuoteTaxRecalculationDto[]): boolean {
  return recalculations.some((recalculation) => !recalculation.recalculated);
}
